import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Paper, Stack, IconButton, Chip,
  CircularProgress, Tooltip,
} from "@mui/material";
import {
  ChevronLeft, ChevronRight, Today as TodayIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  fetchCalendarEvents,
  type CalendarEvent, type LeadStatus,
  STATUS_CONFIG,
} from "./leadApi";
import LeadThreadDrawer from "./LeadThreadDrawer";

const NAVY = "#0d1b2a";
const GOLD = "#c9a96e";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function LeadCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const loadData = useCallback(async () => {
    setLoading(true);
    const from = new Date(year, month, 1).toISOString().split("T")[0];
    const to = new Date(year, month + 1, 0).toISOString().split("T")[0];
    try {
      const data = await fetchCalendarEvents(from, to);
      setEvents(data);
    } catch {}
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  // Build calendar grid
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  const cells: { day: number | null; dateStr: string }[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push({ day: null, dateStr: "" });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, dateStr });
  }

  const getEventsForDate = (dateStr: string) =>
    events.filter((e) => e.start === dateStr);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
            Lead Calendar
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Event dates from active leads
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={prevMonth} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY, minWidth: 180, textAlign: "center" }}>
            {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </Typography>
          <IconButton onClick={nextMonth} size="small">
            <ChevronRight />
          </IconButton>
          <IconButton onClick={goToday} size="small">
            <TodayIcon />
          </IconButton>
          <IconButton onClick={loadData} size="small">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        {(["new", "contacted", "negotiation", "confirmed"] as LeadStatus[]).map((s) => (
          <Stack key={s} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: STATUS_CONFIG[s].color }} />
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {STATUS_CONFIG[s].label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
          {/* Day headers */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {DAYS.map((d) => (
              <Box
                key={d}
                sx={{
                  py: 1,
                  textAlign: "center",
                  bgcolor: NAVY,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                }}
              >
                {d}
              </Box>
            ))}
          </Box>

          {/* Calendar grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {cells.map((cell, i) => {
              const dayEvents = cell.dateStr ? getEventsForDate(cell.dateStr) : [];
              const isToday = cell.dateStr === todayStr;
              return (
                <Box
                  key={i}
                  sx={{
                    minHeight: 100,
                    p: 0.5,
                    borderRight: "1px solid #f1f5f9",
                    borderBottom: "1px solid #f1f5f9",
                    bgcolor: cell.day ? (isToday ? "#fffbeb" : "#fff") : "#f8fafc",
                  }}
                >
                  {cell.day && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: isToday ? 800 : 400,
                          color: isToday ? GOLD : "#64748b",
                          display: "block",
                          px: 0.5,
                        }}
                      >
                        {cell.day}
                      </Typography>
                      <Stack spacing={0.3} sx={{ mt: 0.3 }}>
                        {dayEvents.map((ev) => (
                          <Tooltip
                            key={ev.id}
                            title={`${ev.lead.name} — ${ev.event_type || "Event"} — ${ev.guest_count || "?"} guests`}
                            arrow
                          >
                            <Box
                              onClick={() => {
                                setSelectedId(ev.id);
                                setDrawerOpen(true);
                              }}
                              sx={{
                                bgcolor: ev.color,
                                color: "#fff",
                                px: 0.5,
                                py: 0.2,
                                borderRadius: 0.5,
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                "&:hover": { opacity: 0.85 },
                              }}
                            >
                              {ev.lead.name}
                            </Box>
                          </Tooltip>
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <LeadThreadDrawer
        open={drawerOpen}
        threadId={selectedId}
        onClose={() => setDrawerOpen(false)}
        onUpdated={loadData}
      />
    </Box>
  );
}
